import { Injectable } from '@angular/core';

import { EventsService } from './events.service';
import { FirebaseFunctionsService } from './firebaseFunctions.service';
import { GeolocationService } from '../lib/geolocation.service';
import { SessionService } from '../lib/session.service';
import { UtilsService } from '../lib/utils.service';
import { UxHelperService } from '../lib/uxHelper.service';
import { VariableReplacementService } from '../lib/variableReplacement.service';

import type { TCampaignIncentiveSet, TCoordinate, TCustomer, TSetting } from '@shared/models';
import { Constants } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class SmsService {
    private settings!: TSetting;
    private static areEventHandlersInitialized = false;

    constructor(private sessionService: SessionService, private firebaseFunctionService: FirebaseFunctionsService,
        private variableReplacementService: VariableReplacementService, private eventsService: EventsService,
        private geoLocationService: GeolocationService, private uxHelperService: UxHelperService,
        private utilsService: UtilsService) {
    }

    public async initialize(setting: TSetting) {
        this.settings = setting;

        if (SmsService.areEventHandlersInitialized === false) {
            SmsService.areEventHandlersInitialized = true;

            this.eventsService.subscribe(Constants.EVENT_TICKET_CREATED, async (data: any) => {
                await this.onTicketCreatedEvent(data);
            });
            this.eventsService.subscribe(Constants.EVENT_ALL_ABOARD, async (data: any) => {
                await this.onAllAboardEvent(data);
            });
            this.eventsService.subscribe(Constants.EVENT_APPROACHING_DROPOFF, async (data: any) => {
                await this.onApproachingDropoffEvent(data);
            });
            this.eventsService.subscribe(Constants.EVENT_DROPOFF_COMPLETED, async (data: any) => {
                await this.onDropoffCompletedEvent(data);
            });
            this.eventsService.subscribe(Constants.EVENT_APPROACHING_PICKUP, async (data: any) => {
                await this.onApproachingPickupEvent(data);
            });
            this.eventsService.subscribe(Constants.EVENT_PICKUP_ARRIVED, async (data: any) => {
                await this.onPickupArrivedEvent(data);
            });
            this.eventsService.subscribe(Constants.EVENT_PICKUP_COMPLETED, async (data: any) => {
                await this.onPickupCompletedEvent(data);
            });
            this.eventsService.subscribe(Constants.EVENT_CAMPAIGN_MESSAGE, async (data: any) => {
                await this.onCampaignMessageEvent(data);
            });
            this.eventsService.subscribe(Constants.EVENT_TEST_SMS, async (data: any) => {
                await this.onTestSmsEvent(data);
            });
        }
    }

    public async onTicketCreatedEvent(data: any): Promise<unknown> {
        try {
            if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
                this.settings.ticketCreated.enabled === false) {
                return 0;
            }

            const customer: TCustomer = data.customers[0];
            const debugData = data.debug;
            const rawMessage = this.settings.ticketCreated.rawMessage;

            await this.variableReplacementService.reset();
            this.variableReplacementService.customer = customer;
            this.variableReplacementService.debugData = debugData;
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            return this.firebaseFunctionService.sendSms([customer], 'smsTicketCreated', renderedMessage);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    public async onAllAboardEvent(data: any): Promise<unknown> {
        let response;

        try {
            if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
                this.settings.allAboard.enabled === false) {
                return 0;
            }

            const customers: TCustomer[] = data.customers;
            const debugData = data.debug;
            const rawMessage = this.settings.allAboard.rawMessage;
            const numMinutes = this.settings.allAboard.numMinutes;

            for (const customer of customers) {
                await this.variableReplacementService.reset();
                this.variableReplacementService.customer = customer;
                this.variableReplacementService.numMinutes = numMinutes;
                this.variableReplacementService.debugData = debugData;
                const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

                response = await this.firebaseFunctionService.sendSms([customer], 'smsAllAboard', renderedMessage);
            }
            return response;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    public async onApproachingDropoffEvent(data: any): Promise<unknown> {
        try {
            if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
                this.settings.dropoffApproaching.enabled === false) {
                return 0;
            }

            const customer: TCustomer = data.customers[0];
            const rawMessage = this.settings.dropoffApproaching.rawMessage;
            const debugData = data.debug;

            await this.variableReplacementService.reset();
            this.variableReplacementService.customer = customer;
            this.variableReplacementService.debugData = debugData;
            this.variableReplacementService.numMilesToGo = this.settings.dropoffApproaching.numMilesToGo;
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            return this.firebaseFunctionService.sendSms([customer], 'smsDropoffApproaching', renderedMessage);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    public async onDropoffCompletedEvent(data: any): Promise<unknown> {
        try {
            if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
                this.settings.dropoffCompleted.enabled === false) {
                return 0;
            }

            const customer: TCustomer = data.customers[0];
            const rawMessage = this.settings.dropoffCompleted.rawMessage;
            const debugData = data.debug;

            await this.variableReplacementService.reset();
            this.variableReplacementService.customer = customer;
            this.variableReplacementService.debugData = debugData;
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            return this.firebaseFunctionService.sendSms([customer], 'smsDropoffCompleted', renderedMessage);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    public async onApproachingPickupEvent(data: any): Promise<unknown> {
        try {
            if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
                this.settings.pickupApproaching.enabled === false) {
                return 0;
            }

            const customer: TCustomer = data.customers[0];
            const rawMessage = this.settings.pickupApproaching.rawMessage;
            const debugData = data.debug;
            const toCoordinate: TCoordinate = data.toCoordinate;
            const currentCoordinate: TCoordinate = await this.geoLocationService.getCurrentLocation();

            await this.variableReplacementService.reset();
            this.variableReplacementService.customer = customer;
            this.variableReplacementService.currentCoordinate = currentCoordinate;
            this.variableReplacementService.toCoordinate = toCoordinate;
            this.variableReplacementService.debugData = debugData;
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            return this.firebaseFunctionService.sendSms([customer], 'smsPickupApproaching', renderedMessage);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    public async onPickupArrivedEvent(data: any): Promise<unknown> {
        try {
            if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
                this.settings.pickupArrived.enabled === false) {
                return 0;
            }

            const customer: TCustomer = data.customers[0];
            const rawMessage = this.settings.pickupArrived.rawMessage;
            const debugData = data.debug;
            await this.variableReplacementService.reset();
            this.variableReplacementService.customer = customer;
            this.variableReplacementService.debugData = debugData;
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            return this.firebaseFunctionService.sendSms([customer], 'smsPickupArrived', renderedMessage);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    public async onPickupCompletedEvent(data: any) {
        try {
            if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
                this.settings.pickupApproaching.enabled === false) {
                return 0;
            }

            const customer: TCustomer = data.customers[0];
            const rawMessage = this.settings.pickupCompleted.rawMessage;
            const debugData = data.debug;

            await this.variableReplacementService.reset();
            this.variableReplacementService.customer = customer;
            this.variableReplacementService.debugData = debugData;
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            return this.firebaseFunctionService.sendSms([customer], 'smsPickupCompleted', renderedMessage);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    // This is called when the logged in user wants to send the campaign message to himself.
    public async onCampaignMessageEvent(data: any): Promise<unknown> {
        try {
            if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
                this.settings.campaignMessage.enabled === false) {
                return 0;
            }

            const customer: TCustomer = data.customers[0];
            const rawMessage = this.settings.campaignMessage.rawMessage;
            const debugData = data.debug;

            await this.variableReplacementService.reset();
            this.variableReplacementService.customer = customer;
            this.variableReplacementService.debugData = debugData;
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            return this.firebaseFunctionService.sendSms([customer], 'smsPickupCompleted', renderedMessage);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    // This is called to send the campaign to 'real' customers.
    public async sendCampaignMessage(data: any): Promise<unknown> {
        try {
            if (this.sessionService.getAccountProfile().featuresInfo.campaign === false ||
                this.settings.campaignMessage.enabled === false) {
                return 0;
            }

            const rawMessage = this.settings.campaignMessage.rawMessage;
            const debugData = data.debug;
            const customer = data.customers[0];
            const incentiveSet = data.incentiveSet as TCampaignIncentiveSet;

            // The customer may have opted out of receiving these incentives in which case he is no longer active.
            if (!customer.isActive) {
                return null;
            }

            await this.variableReplacementService.reset();
            this.variableReplacementService.customer = customer;
            this.variableReplacementService.incentiveSetId = incentiveSet.incentiveSetId;
            this.variableReplacementService.debugData = debugData;
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            return this.firebaseFunctionService.sendSms([customer], 'smsCampaign', renderedMessage);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }

    public async onTestSmsEvent(data: any): Promise<unknown> {
        let response;

        try {
            // if (this.sessionService.getAccountProfile().featuresInfo.customer === false ||
            //     this.settings.allAboard.enabled === false) {
            //     return 0;
            // }

            const customers: TCustomer[] = data.customers;
            // const debugData = data.debug;
            const rawMessage = data.debug;

            for (const customer of customers) {
                await this.variableReplacementService.reset();
                this.variableReplacementService.customer = customer;
                // this.variableReplacementService.debugData = debugData;
                const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

                response = await this.firebaseFunctionService.sendSms([customer], data.smsType, renderedMessage);
            }
            return response;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Warning', errorMessage);
            return null;
        }
    }
}
